import $api from "@/api/ApiInteceptor";
import { IAppVersion } from "@/struct/IAppVersion/IAppVersion";
import { ITrustedDevice } from "@/struct/IDevice/IDevice";
import { IAuthResponse } from "@/struct/ILogin/ILogin";
import { EWebMethod } from "@/struct/IRequest/IRequest";
import { IAppInfo, IBrandStatus, IUserData } from "@/struct/IStore/IStore";
import { Capacitor } from "@capacitor/core";

export default class ApiEnter {
  public static async getBrandStatus(): Promise<IBrandStatus | boolean> {
    try {
      const response = await $api.call(
        EWebMethod.GET,
        "/api/v4/mobile/driver/brend-subdomain",
      );
      return response.data;
    } catch (error) {
      return false;
    }
  }

  public static async getAppInfo(): Promise<IAppInfo | boolean> {
    try {
      const response = await $api.call(
        EWebMethod.GET,
        "/auth/application/brend",
      );
      return response.data;
    } catch (error) {
      return false;
    }
  }

  public static async getYandexAppEnabled(): Promise<boolean> {
    if (Capacitor.getPlatform() == "ios") {
      try {
        const response = await $api.call(
          EWebMethod.GET,
          "/auth/application/yandex-app/enabled",
        );
        return response.data;
      } catch (error) {
        return false;
      }
    } else {
      return true;
    }
  }

  public static async getAccountUser(): Promise<IUserData | boolean> {
    try {
      const response = await $api.call(
        EWebMethod.GET,
        "/api/v4/mobile/driver/profile",
      );
      return response.data;
    } catch (error) {
      return false;
    }
  }

  public static async getAppVersion(): Promise<IAppVersion> {
    const response = await $api.call(
      EWebMethod.GET,
      `/auth/user/check-version`,
    );
    return response?.data;
  }

  public static async getTrustedDevices(): Promise<ITrustedDevice[] | boolean> {
    try {
      const response = await $api.call(EWebMethod.GET, "/auth/devices");
      return response.data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Возвращает тело ответа как есть (включая тела ошибок бека
   * { detail, message, exception_id }), чтобы вызывающий код мог
   * показать message пользователю. Успех: detail === "OK".
   */
  public static async requestDeviceLocations(
    deviceIds: string[],
  ): Promise<any> {
    try {
      const response = await $api.call(
        EWebMethod.POST,
        "/auth/devices/locations",
        deviceIds,
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Возвращает тело ответа как есть (включая тела ошибок бека
   * { detail, message, exception_id }), чтобы вызывающий код мог
   * показать message пользователю. Успех: detail === "OK".
   */
  public static async deauthorizeDevice(deviceId: string): Promise<any> {
    try {
      const response = await $api.call(
        EWebMethod.POST,
        `/auth/devices/${deviceId}/deauthorize`,
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  }

  /**
   * См. deauthorizeDevice: тело ответа возвращается как есть.
   * Успех: detail === "OK".
   */
  public static async deleteDevice(deviceId: string): Promise<any> {
    try {
      const response = await $api.call(
        EWebMethod.DELETE,
        `/auth/devices/${deviceId}/delete`,
      );
      return response?.data;
    } catch (error) {
      return false;
    }
  }

  public static async postYandexLoginCallback(
    phone: string,
    access_token: string,
    password: string,
  ): Promise<IAuthResponse> {
    try {
      const response = await $api.call(
        EWebMethod.POST,
        "/auth/user/yandex-login-callback",
        {
          token: access_token,
          username: phone,
          password: password ? password : null,
        },
      );
      return response.data as IAuthResponse;
    } catch (error) {
      return {
        error: "request_failed",
        error_description: "Ошибка входа в Яндекс.",
        detail: "API_REQUEST_ERROR",
      };
    }
  }

  static async postYandexActivationCallback(
    username: string,
    token: string,
    password: string,
  ): Promise<IAuthResponse> {
    try {
      const response = await $api.call(
        EWebMethod.POST,
        "/auth/user/yandex-login-callback",
        {
          username,
          token,
          password,
        },
      );
      return response.data as IAuthResponse;
    } catch (error) {
      return {
        error: "request_failed",
        error_description: "Ошибка активации через Яндекс.",
        detail: "API_REQUEST_ERROR",
      };
    }
  }

  public static async getYandexCode(code: string): Promise<IAuthResponse> {
    try {
      const response = await $api.call(
        EWebMethod.GET,
        `/auth/user/login/yandex-auth/callback/?code=${code}`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return {
        error: "request_failed",
        error_description: "API request error",
      };
    }
  }

  public static async activateWithYandex(
    code: string,
    username: string,
    password: string,
  ): Promise<IAuthResponse> {
    try {
      const response = await $api.call(
        EWebMethod.POST,
        "/auth/user/login/confirmed/yandex-activate",
        {
          code: code,
          username: username,
          password: password,
        },
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return {
        error: "activation_failed",
        error_description: "Account activation error",
      };
    }
  }

  public static async validateEnter(phone_number: string) {
    try {
      const response = await $api.call(
        EWebMethod.GET,
        `/auth/is-activate?username=${phone_number}`,
      );
      return response.data;
    } catch {
      return false;
    }
  }

  public static async activateUser(
    phone: string,
    password: string,
  ): Promise<{
    detail: string;
    otp_status: string;
    phone_number_call_confirmed: string;
    confirmation_id: string;
  }> {
    const response = await $api.call(EWebMethod.POST, "/auth/v2/activate", {
      username: phone,
      password: password,
    });
    return response?.data;
  }

  public static async loginUser(
    phone: string,
    password: string,
  ): Promise<
    IAuthResponse & {
      phone_number_call_confirmed?: string;
      confirmation_id?: string;
    }
  > {
    const response = await $api.call(
      EWebMethod.POST,
      "/auth/v2/user/login-mobile",
      {
        username: phone,
        password: password,
      },
    );
    return response?.data;
  }

  public static async finalizeConfirmation(
    confirmationId: string,
  ): Promise<IAuthResponse> {
    const response = await $api.call(
      EWebMethod.POST,
      `/auth/confirmed/${confirmationId}/finalize`,
    );
    return response?.data;
  }

  public static async logout(): Promise<boolean> {
    try {
      await $api.call(EWebMethod.POST, "/auth/user/logout");
      return true;
    } catch {
      return false;
    }
  }

  public static async sendCode(otp: string) {
    const response = await $api.call(
      EWebMethod.POST,
      "/auth/user/login/confirmed",
      {
        code: otp,
      },
    );
    return response.data;
  }

  public static async sendCodeActivation(otp: string) {
    const response = await $api.call(EWebMethod.POST, "/auth/confirmed", {
      code: otp,
    });
    return response.data;
  }

  public static async confirmByCode(
    confirmationId: string,
    otp: string,
  ): Promise<any> {
    const response = await $api.call(
      EWebMethod.POST,
      `/auth/confirmed/${confirmationId}`,
      { code: otp },
    );
    return response?.data;
  }

  public static async checkCallStatus(confirmationId: string): Promise<any> {
    const response = await $api.call(
      EWebMethod.GET,
      `/auth/confirmed/${confirmationId}/status`,
    );
    return response?.data;
  }
}
